import { useCallback } from "react";
import { type GridEventListener, type GridRowId } from "@mui/x-data-grid-pro";
import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "../utils/acquirePolicy";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  isAllMattersField,
} from "../utils/checkboxFields";
import {
  getBulkCellDisabledReason,
  isBulkCellDisabled,
} from "../utils/cellAvailability";
import {
  eligibleZenbujikoConstructions,
  isConstructionMode,
  pickConstructionForToggle,
} from "../utils/zenbujikoConstruction";
import { type CheckedIdsStore } from "./useCheckedIdsStore";

export interface BulkCheckToggleParams {
  store: CheckedIdsStore;
  /** 表示中の行。null は読み込み前 */
  filteredRows: BulkPropertyRow[] | null;
  /** 表示中の行ID。null は全行表示 */
  visibleRowIds: Set<GridRowId> | null;
  policy: AcquirePolicy;
  pastDays: number;
  excludeAcquired: boolean;
}

/**
 * グリッドのチェック操作。セル単位のトグルと列ヘッダーのダブルクリック一括切替を
 * 取得条件（全部事項の候補構成・可否）に合わせて行う
 */
export const useBulkCheckToggle = ({
  store,
  filteredRows,
  visibleRowIds,
  policy,
  pastDays,
  excludeAcquired,
}: BulkCheckToggleParams): {
  onToggle: (field: string, row: BulkPropertyRow) => void;
  onColumnHeaderDoubleClick: GridEventListener<"columnHeaderDoubleClick">;
} => {
  // 全部事項の構成抽出。onToggle と onColumnHeaderDoubleClick の
  // 両方が同じ引数列で呼ぶため共通化する
  const constructionsFor = useCallback(
    (row: BulkPropertyRow, now: Date): string[][] =>
      eligibleZenbujikoConstructions(
        row,
        policy,
        pastDays,
        now,
        excludeAcquired
      ),
    [policy, pastDays, excludeAcquired]
  );

  // チェック操作。全部事項はリユースのみ・取得済みのみのとき、候補の構成に4列をそろえる
  const onToggle = useCallback(
    (field: string, row: BulkPropertyRow): void => {
      const constructions = constructionsFor(row, new Date());
      if (!isAllMattersField(field) || constructions.length === 0) {
        store.toggle(field, row.id);
        return;
      }
      const current = store.getRowFields(ALL_MATTERS_FIELDS, row.id);
      store.setRowFieldsBulk(ALL_MATTERS_FIELDS, [
        {
          id: row.id,
          checkedFields:
            pickConstructionForToggle(constructions, current, field) ?? [],
        },
      ]);
    },
    [store, constructionsFor]
  );

  // 列ヘッダーのダブルクリックで、その列のチェックボックスを一括切り替えする
  // （全行チェック済みなら全解除、それ以外は全行チェック）
  const onColumnHeaderDoubleClick: GridEventListener<"columnHeaderDoubleClick"> =
    useCallback(
      (params) => {
        if (
          filteredRows == null ||
          !CHECKBOX_FIELDS.some(({ field }) => field === params.field)
        ) {
          return;
        }
        const closureCheckedIds = store.getField("requestClosureBook");
        const now = new Date();
        const checked = store.getField(params.field);

        // 全部事項を構成単位で扱う方式では、行ごとに成立する構成を当てはめる
        // （列単位で一括チェックすると構成をまたいで対象外になる）
        if (isConstructionMode(policy) && isAllMattersField(params.field)) {
          // 構成抽出（eligibleZenbujikoConstructions）は選択可否とチェック内容の
          // 両方に使うため、行ごとに1回だけ行い使い回す
          const rowsWithConstructions = filteredRows.map(
            (row) => [row, constructionsFor(row, now)] as const
          );
          // 選択不可（種別・閉鎖との排他・リユース候補なし）の行は全選択の対象にしない。
          // 閉鎖との排他は候補構成に現れないため isBulkCellDisabled 側の判定を残す
          const selectableEntries = rowsWithConstructions.filter(
            ([row, constructions]) =>
              getBulkCellDisabledReason(
                params.field,
                row,
                closureCheckedIds,
                excludeAcquired
              ) == null &&
              constructions.some((fields) => fields.includes(params.field))
          );
          const allChecked =
            selectableEntries.length > 0 &&
            selectableEntries.every(([row]) => checked.has(row.id));
          const entries = selectableEntries.map(([row, constructions]) => {
            const current = store.getRowFields(ALL_MATTERS_FIELDS, row.id);
            const construction = pickConstructionForToggle(
              constructions,
              current,
              params.field
            );
            return {
              id: row.id,
              checkedFields: allChecked ? [] : construction ?? current,
            };
          });
          store.setRowFieldsBulk(ALL_MATTERS_FIELDS, entries);
          return;
        }

        // 選択不可（種別・リユース候補なし）の行は全選択の対象にしない
        const selectableRows = filteredRows.filter(
          (row) =>
            !isBulkCellDisabled(
              params.field,
              row,
              policy,
              pastDays,
              closureCheckedIds,
              now,
              excludeAcquired
            )
        );
        const allChecked =
          selectableRows.length > 0 &&
          selectableRows.every((row) => checked.has(row.id));
        // 絞り込みで非表示の行は選択状態を保持したまま可視行分だけ置き換える
        const hiddenChecked = [...checked].filter(
          (id) => !visibleRowIds?.has(id)
        );
        store.setField(
          params.field,
          new Set<GridRowId>([
            ...hiddenChecked,
            ...(allChecked ? [] : selectableRows.map((row) => row.id)),
          ])
        );
      },
      [
        filteredRows,
        visibleRowIds,
        store,
        policy,
        pastDays,
        excludeAcquired,
        constructionsFor,
      ]
    );

  return { onToggle, onColumnHeaderDoubleClick };
};
